<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { dynamicTime } from 'shared/helpers/timeHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  contacts: { type: Array, default: () => [] },
  selectedIds: { type: Array, default: () => [] },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
});

const emit = defineEmits([
  'showContact',
  'toggleSelection',
  'toggleSelectAll',
  'update:sort',
]);

const { t } = useI18n();
const store = useStore();

// Só as colunas que o backend sabe ordenar recebem header clicável
// (mesmas chaves usadas em ContactSortMenu/sortAttr); as demais são só rótulos.
const COLUMNS = [
  {
    key: 'name',
    label: 'CONTACTS_LAYOUT.TABLE.CONTACT',
    sortable: true,
    align: 'left',
  },
  {
    key: 'email',
    label: 'CONTACTS_LAYOUT.TABLE.EMAIL',
    sortable: true,
    align: 'left',
  },
  {
    key: 'phone_number',
    label: 'CONTACTS_LAYOUT.TABLE.PHONE',
    sortable: false,
    align: 'left',
  },
  {
    key: 'company_name',
    label: 'CONTACTS_LAYOUT.TABLE.COMPANY',
    sortable: true,
    align: 'left',
  },
  {
    key: 'labels',
    label: 'CONTACTS_LAYOUT.TABLE.LABELS',
    sortable: false,
    align: 'left',
  },
  {
    key: 'last_activity_at',
    label: 'CONTACTS_LAYOUT.TABLE.LAST_ACTIVITY',
    sortable: true,
    align: 'right',
  },
];

const allSelected = computed(
  () =>
    props.contacts.length > 0 &&
    props.contacts.every(c => props.selectedIds.includes(c.id))
);

const someSelected = computed(
  () => props.selectedIds.length > 0 && !allSelected.value
);

// Labels não vêm no payload da listagem (só via endpoint dedicado por
// contato), então buscamos sob demanda e lemos do módulo `contactLabels`,
// igual ao que ContactLabels.vue já faz para a tela de detalhe.
const fetchedLabelIds = new Set();

const ensureLabelsLoaded = id => {
  if (fetchedLabelIds.has(id)) return;
  fetchedLabelIds.add(id);
  store.dispatch('contactLabels/get', id);
};

const rows = computed(() =>
  props.contacts.map(contact => {
    ensureLabelsLoaded(contact.id);
    const labels =
      store.getters['contactLabels/getContactLabels'](contact.id) || [];

    return {
      id: contact.id,
      name: contact.name || t('CONTACTS_LAYOUT.TABLE.UNNAMED'),
      email: contact.email || '',
      phoneNumber: contact.phoneNumber || '',
      avatarUrl: contact.thumbnail || null,
      availabilityStatus: contact.availabilityStatus || null,
      companyName: contact.additionalAttributes?.companyName || '',
      labels,
      lastActivityLabel: contact.lastActivityAt
        ? dynamicTime(contact.lastActivityAt)
        : '',
    };
  })
);

const isSelected = id => props.selectedIds.includes(id);

const onToggleSelectAll = () => emit('toggleSelectAll', !allSelected.value);

const onToggleRow = (id, value) => emit('toggleSelection', { id, value });

const onSort = column => {
  if (!column.sortable) return;
  // 3-way não; alterna asc/desc na coluna, default asc ao trocar de coluna.
  const isActive = props.activeSort === column.key;
  const order = isActive && props.activeOrdering !== '-' ? '-' : '';
  emit('update:sort', { sort: column.key, order });
};

const sortIcon = column => {
  if (!column.sortable || props.activeSort !== column.key) return null;
  return props.activeOrdering === '-'
    ? 'i-lucide-arrow-down'
    : 'i-lucide-arrow-up';
};
</script>

<template>
  <div class="w-full overflow-x-auto">
    <table class="w-full text-sm border-collapse min-w-[820px]">
      <thead>
        <tr class="border-b border-n-weak">
          <th class="w-10 py-2.5 ltr:pl-6 rtl:pr-6">
            <Checkbox
              :model-value="allSelected"
              :indeterminate="someSelected"
              @update:model-value="onToggleSelectAll"
            />
          </th>
          <th
            v-for="column in COLUMNS"
            :key="column.key"
            class="py-2.5 px-3 text-xs font-medium tracking-wide uppercase text-n-slate-10 whitespace-nowrap select-none"
            :class="[
              column.align === 'right' ? 'text-right' : 'text-left',
              column.sortable ? 'cursor-pointer hover:text-n-slate-12' : '',
            ]"
            @click="onSort(column)"
          >
            <span
              class="inline-flex items-center gap-1"
              :class="column.align === 'right' ? 'flex-row-reverse' : ''"
            >
              {{ t(column.label) }}
              <Icon
                v-if="sortIcon(column)"
                :icon="sortIcon(column)"
                class="size-3 text-n-blue-text"
              />
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in rows"
          :key="row.id"
          class="border-b cursor-pointer group border-n-weak/60 transition-colors hover:bg-n-alpha-1"
          :class="isSelected(row.id) ? 'bg-n-blue-2' : ''"
          @click="emit('showContact', row.id)"
        >
          <td class="py-2.5 ltr:pl-6 rtl:pr-6" @click.stop>
            <Checkbox
              :model-value="isSelected(row.id)"
              @update:model-value="value => onToggleRow(row.id, value)"
            />
          </td>
          <td class="py-2.5 px-3">
            <div class="flex items-center min-w-0 gap-2.5">
              <Avatar
                :name="row.name"
                :src="row.avatarUrl"
                :status="row.availabilityStatus"
                :size="32"
                class="shrink-0"
                hide-offline-status
                rounded-full
              />
              <span
                class="font-medium truncate text-n-slate-12 group-hover:text-n-blue-text"
              >
                {{ row.name }}
              </span>
            </div>
          </td>
          <td class="py-2.5 px-3 text-n-slate-11 whitespace-nowrap">
            <span class="truncate">{{ row.email || '—' }}</span>
          </td>
          <td class="py-2.5 px-3 text-n-slate-11 whitespace-nowrap">
            {{ row.phoneNumber || '—' }}
          </td>
          <td class="py-2.5 px-3 text-n-slate-11 whitespace-nowrap">
            <span class="line-clamp-1">{{ row.companyName || '—' }}</span>
          </td>
          <td class="py-2.5 px-3">
            <div
              v-if="row.labels.length"
              class="flex flex-wrap items-center gap-1 max-w-[12rem]"
            >
              <span
                v-for="label in row.labels.slice(0, 2)"
                :key="label.id"
                class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-n-alpha-2 text-xs text-n-slate-11 whitespace-nowrap"
              >
                <span
                  class="rounded-full size-1.5 shrink-0"
                  :style="{ backgroundColor: label.color }"
                />
                {{ label.title }}
              </span>
              <span
                v-if="row.labels.length > 2"
                class="text-xs text-n-slate-10 whitespace-nowrap"
              >
                +{{ row.labels.length - 2 }}
              </span>
            </div>
            <span v-else class="text-n-slate-9">—</span>
          </td>
          <td
            class="py-2.5 ltr:pr-6 rtl:pl-6 px-3 text-right text-n-slate-10 whitespace-nowrap"
          >
            {{ row.lastActivityLabel || '—' }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
